'use client';

import { ProfileHeader } from './ProfileHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Quote, Briefcase, Heart, Share2, ArrowRight } from 'lucide-react';
import {
  getProfileUrl,
  type PublicUserWithCommunities,
  type UserBusinessProfile,
} from '@/types/profile';
import { ProfileLink } from './ProfileLink';
import { Button } from '@/components/ui/button';

import { useUser } from '@/hooks/use-user';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { stripHtmlToPlainText } from '@/lib/strip-html';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { SocialLinks } from '@/components/social/social-links';

interface UserBusinessProfileViewProps {
  userBusiness: UserBusinessProfile;
  isEditable?: boolean;
  onEdit?: () => void;
}

export function UserBusinessProfileView({
  userBusiness,
  isEditable,
  onEdit,
}: UserBusinessProfileViewProps) {
  const { user, business } = userBusiness;
  const { communities: myCommunities } = useUser();
  const navigate = useNavigate();
  const { communityId } = useParams();

  const commonCommunities =
    (user as unknown as PublicUserWithCommunities).communities?.filter((c) =>
      myCommunities.some((myC) => myC.id === c.id)
    ) || [];

  const businessStandaloneHref = communityId
    ? `/${communityId}${getProfileUrl({ businessId: business.id })}`
    : getProfileUrl({ businessId: business.id });

  return (
    <div className="space-y-4">
      <ProfileHeader
        type="user-business"
        user={user}
        business={business}
        userBusiness={userBusiness}
        isEditable={isEditable}
        onEdit={onEdit}
      />

      <div className="max-w-5xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-3 gap-6 pb-12">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* Founder Story / Bio */}
          <Card className="glass-card border-white/20 rounded-3xl shadow-xl overflow-hidden relative">
            <div className="absolute top-0 right-0 p-6 opacity-10">
              <Quote className="h-24 w-24 text-primary" />
            </div>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-black font-display tracking-tight">
                The Founder Story
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg text-muted-foreground leading-relaxed italic font-medium">
                {userBusiness.founderStory ||
                  'Every business has a story. This founder is dedicated to building something impactful and creating value for the community.'}
              </p>
            </CardContent>
          </Card>

          {/* Business Link Card */}
          <Card
            className="glass-card border-white/20 rounded-3xl shadow-lg overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10 group hover:shadow-2xl transition-all cursor-pointer"
            onClick={() => navigate(businessStandaloneHref)}
          >
            <CardContent className="p-4 md:p-6 flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <Avatar className="h-24 w-24 md:h-32 md:w-32 rounded-3xl shadow-xl group-hover:scale-105 transition-transform duration-500 ring-4 ring-white">
                <AvatarImage src={business.logo} className="object-cover" />
                <AvatarFallback className="bg-primary text-white text-3xl font-black">
                  {business.name[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-center md:text-left space-y-4">
                <div>
                  <h3 className="text-2xl font-black font-display text-foreground tracking-tight">
                    {business.name}
                  </h3>
                  <p className="text-muted-foreground font-medium">
                    {formatBusinessCategoryLabel(
                      business.category,
                      business.subCategory,
                      business.selfDefinedCategory
                    )}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {stripHtmlToPlainText(
                    [business.description, business.speciality].filter(Boolean).join(' ')
                  ) || 'Explore the business profile for more details.'}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <ProfileLink businessId={business.id} showAvatar={false} showName={false}>
                    <Button variant="default" size="sm" className="rounded-xl font-bold gap-2">
                      View Business Profile
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </ProfileLink>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Founder Quick Info */}
          <Card className="glass-card border-white/20 rounded-3xl shadow-xl overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-black font-display tracking-tight">
                Quick Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ProfileLink
                userId={user.id}
                image={user.image}
                name={`${user.firstName} ${user.lastName}`}
                className="w-full"
              >
                <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/30 border border-white/20 w-full hover:bg-white/50 transition-all">
                  <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                    <AvatarImage src={user.image} className="object-cover" />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {user.firstName[0]}
                      {user.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="text-xs font-black text-foreground leading-tight">
                      View User Profile
                    </p>
                    <p className="text-[10px] text-muted-foreground">Founder & Leader</p>
                  </div>
                </div>
              </ProfileLink>

              <div className="p-3 rounded-2xl bg-white/30 border border-white/20 flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Briefcase className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 leading-none mb-1">
                    Role
                  </p>
                  <p className="text-sm font-bold text-foreground">{userBusiness.role}</p>
                </div>
              </div>

              {user.socialLinks && Object.keys(user.socialLinks).length > 0 && (
                <div className="p-3 rounded-2xl bg-white/30 border border-white/20 space-y-2">
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 leading-none">
                    Social Media
                  </p>
                  <SocialLinks socialLinks={user.socialLinks} size="sm" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Common Communities in Sidebar */}
          {commonCommunities.length > 0 && (
            <Card className="glass-card border-white/20 rounded-3xl shadow-xl overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-black font-display tracking-tight">
                  Common Communities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {commonCommunities.map((community) => (
                  <Link
                    key={community.id}
                    to={`/${community.id}/dashboard`}
                    className="block group"
                  >
                    <div className="flex items-center gap-3 p-2 rounded-2xl bg-white/30 border border-white/10 hover:bg-white/50 transition-all">
                      <Avatar className="h-8 w-8 rounded-lg shadow-sm">
                        <AvatarImage src={community.logo} className="object-cover" />
                        <AvatarFallback>
                          <Users className="h-4 w-4 text-primary" />
                        </AvatarFallback>
                      </Avatar>
                      <p className="text-xs font-bold text-foreground truncate group-hover:text-primary transition-colors">
                        {community.name}
                      </p>
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Engagement Card */}
          <div className="p-6 rounded-3xl bg-gradient-to-br from-primary to-primary/70 text-white shadow-2xl shadow-primary/20 space-y-6">
            <h3 className="text-lg font-black font-display leading-tight">
              Connect with this Founder
            </h3>
            <div className="flex gap-4">
              <Button
                size="icon"
                variant="secondary"
                className="h-12 w-12 rounded-2xl bg-white/20 border-white/20 text-white hover:bg-white hover:text-primary transition-all"
              >
                <Heart className="h-6 w-6" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="h-12 w-12 rounded-2xl bg-white/20 border-white/20 text-white hover:bg-white hover:text-primary transition-all"
              >
                <Share2 className="h-6 w-6" />
              </Button>
              <Button className="flex-1 h-12 rounded-2xl bg-white text-primary font-black hover:bg-white/90 transition-all">
                Message
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
